// Import store and actions
import { store } from '../store';
import { loginAdmin as loginAdminAction, clearAuth, setAdminUser, setTokens } from '../store/slices/authSlice';
import { apiService } from './apiService';
import { persistor } from '../store';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getClientAppGateHeaders } from '../utils/clientAppGate';

interface AdminUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  role: string;
  department: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';
  permissions: string[];
  lastLogin?: string;
  createdDate: string;
  createdBy: string;
  avatarBase64?: string;
}

interface LoginResponse {
  status: string;
  code: number;
  message: string;
  data: {
    accessToken?: string;
    refreshToken?: string;
    role: string;
    permissions: string[];
    department: string;
    fullName?: string;
  };
}

interface SetupResponse {
  status: string;
  code: number;
  message: string;
  data: {
    hasAdminUsers: boolean;
    message: string;
  };
}

class AdminAuthService {
  private getState() {
    return store.getState().auth;
  }

  async getSetupStatus(): Promise<SetupResponse['data']> {
    const response = await apiService.get<SetupResponse['data']>('/admin/setup/status');
    if (!response.success) {
      throw new Error('Failed to check setup status');
    }
    return response.data;
  }

  async initializeSystem(setupData: {
    username: string;
    password: string;
    email: string;
    firstName: string;
    lastName: string;
  }): Promise<any> {
    const response = await apiService.post('/admin/setup/initialize', setupData);
    if (!response.success) {
      throw new Error(response.error || 'Failed to initialize system');
    }
    return response.data;
  }

  async login(username: string, password: string, useSession: boolean = true): Promise<AdminUser> {
    try {
      const headers = {
        'X-Type': useSession ? 'web' : 'api'
      };

      const response = await apiService.post<LoginResponse['data']>(
        '/admin/auth/login',
        { username, password },
        { headers }
      );

      if (!response.success) {
        throw new Error(response.error || 'Login failed');
      }

      const result = response.data;
      
      console.log('Login response:', result);
      
      const adminUser: AdminUser = {
        id: username, // Use username as ID for now
        username,
        email: username, // Use username as email for now
        firstName: result.fullName?.split(' ')[0] || username,
        lastName: result.fullName?.split(' ').slice(1).join(' ') || '',
        fullName: result.fullName || `${username}`,
        role: result.role,
        department: result.department,
        status: 'ACTIVE',
        permissions: result.permissions || [],  // Ensure permissions is always an array
        lastLogin: new Date().toISOString(),
        createdDate: new Date().toISOString(),
        createdBy: 'system',
        avatarBase64: undefined
      };
      
      console.log('Created admin user:', adminUser);

      // Dispatch admin user to Redux store
      store.dispatch(setAdminUser(adminUser));

      // For JWT-based authentication, save tokens to Redux
      if (!useSession && result.accessToken) {
        // CRITICAL FIX: Clear session cookies when using JWT auth
        // This prevents session cookies from being sent with JWT requests
        this.clearSessionCookies();
        
        store.dispatch(setTokens({
          accessToken: result.accessToken,
          refreshToken: result.refreshToken || null,
          type: 'JWT'
        }));
      } else {
        // For session-based authentication
        store.dispatch(setTokens({
          accessToken: null,
          refreshToken: null,
          type: 'SESSION'
        }));
      }

      return adminUser;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  // Utility function to clear session cookies (JSESSIONID, ADMIN_SESSION)
  private clearSessionCookies(): void {
    try {
      const domain = window.location.hostname;
      const sessionCookies = ['JSESSIONID', 'ADMIN_SESSION', 'RETAIL_SESSION'];
      
      sessionCookies.forEach(cookieName => {
        // Clear cookie for current path
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
        
        // Also try clearing for root domain
        if (domain && !domain.includes('localhost') && !domain.includes('127.0.0.1')) {
          // Try with domain
          document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain};`;
          // Try with leading dot for subdomain support
          document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${domain};`;
        }
      });
      
      console.log('Session cookies cleared (JSESSIONID, ADMIN_SESSION, RETAIL_SESSION)');
    } catch (error) {
      console.error('Error clearing session cookies:', error);
    }
  }

  // Utility function to clear all cookies (including non-HTTP-only ones)
  private clearAllCookies(): void {
    try {
      // Get all cookies
      const cookies = document.cookie.split(';');
      
      // Clear each cookie by setting it to expire
      cookies.forEach(cookie => {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        
        // Clear cookie for current path
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
        
        // Also try clearing for root domain
        const domain = window.location.hostname;
        if (domain && !domain.includes('localhost') && !domain.includes('127.0.0.1')) {
          // Try with domain
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain};`;
          // Try with leading dot for subdomain support
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${domain};`;
        }
      });
      
      console.log('All cookies cleared (non-HTTP-only)');
    } catch (error) {
      console.error('Error clearing cookies:', error);
    }
  }

  async logout(): Promise<void> {
    try {
      const state = this.getState();
      const token = state.token;
      
      // Attempt to call logout endpoint if we have a token
      // This will clear HTTP-only cookies on the server
      if (token && token !== 'SESSION_AUTH') {
        try {
          const response = await fetch(`${getApiBaseUrl()}/admin/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              ...getClientAppGateHeaders(),
            },
            credentials: 'include', // Include cookies for session logout too
          });
          console.log('Admin logout API response:', response.status);
        } catch (error) {
          console.error('Admin logout API call error:', error);
          // Continue with cleanup even if API call fails
        }
      } else if (token === 'SESSION_AUTH') {
        // For session auth, try to logout via session endpoint
        try {
          const response = await fetch(`${getApiBaseUrl()}/admin/auth/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getClientAppGateHeaders(),
            },
            credentials: 'include', // Important for session cookies
          });
          console.log('Admin session logout API response:', response.status);
        } catch (error) {
          console.error('Admin session logout API call error:', error);
          // Continue with cleanup even if API call fails
        }
      } else {
        // Try logout endpoint anyway
        try {
          await apiService.post('/admin/auth/logout');
        } catch (error) {
          console.error('Admin logout request failed:', error);
        }
      }
      
      // Small delay to ensure server has processed logout and cleared cookies
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (error) {
      console.error('Admin logout error:', error);
    } finally {
      // Clear Redux store
      store.dispatch(clearAuth());
      
      // Clear all cookies (non-HTTP-only ones that can be cleared via JavaScript)
      this.clearAllCookies();
      
      // Clear all localStorage items related to auth
      try {
        // Clear context-aware auth storage keys
        localStorage.removeItem('auth-retail');
        localStorage.removeItem('auth-admin');
        // Also clear legacy keys if they exist
        localStorage.removeItem('auth');
        localStorage.removeItem('persist:auth');
        localStorage.removeItem('persist:auth-retail');
        localStorage.removeItem('persist:auth-admin');
        
        // Clear admin-specific localStorage items
        localStorage.removeItem('adminActiveTab');
        localStorage.removeItem('debugPanelPosition');
        
        // Clear any other auth-related items
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (
            key.startsWith('auth') || 
            key.includes('token') || 
            key.includes('refresh') ||
            key.includes('session') ||
            key.startsWith('admin') ||
            key.startsWith('Admin')
          )) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => {
          try {
            localStorage.removeItem(key);
            console.log('Cleared localStorage key:', key);
          } catch (e) {
            console.error('Error clearing localStorage key:', key, e);
          }
        });
        
        console.log('All admin auth-related localStorage items cleared');
      } catch (error) {
        console.error('Error clearing localStorage:', error);
      }
      
      // Clear sessionStorage as well
      try {
        sessionStorage.clear();
        console.log('SessionStorage cleared');
      } catch (error) {
        console.error('Error clearing sessionStorage:', error);
      }
      
      // Purge redux-persist storage for auth
      try {
        await persistor.purge();
        console.log('Redux-persist storage purged');
      } catch (error) {
        console.error('Error purging redux-persist storage:', error);
      }
      
      // Additional delay to ensure all cleanup is complete
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  getCurrentAdmin(): AdminUser | null {
    return this.getState().adminUser;
  }

  getAccessToken(): string | null {
    return this.getState().token;
  }

  isAuthenticated(): boolean {
    return this.getState().isAuthenticated;
  }

  hasPermission(permission: string): boolean {
    const adminUser = this.getState().adminUser;
    if (!adminUser) return false;
    return adminUser.permissions.includes(permission);
  }
}

export const adminAuthService = new AdminAuthService();
export type { AdminUser }; 